import React, { useState, useEffect, useRef } from 'react';
import { usePostContext } from '../context/PostContext';
import { useUserContext } from '../context/UserContext';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import EmojiPicker from 'emoji-picker-react';
import UserAvatar from './UserAvatar';

const GIPHY_KEY = '8FL5QcGl28D9gd1GvoG246swmDsiZLja';

const CommentBox = ({ post, setShowCommentBox, comment_id, GetPoost }) => {
    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));
    const { addCommentToPost } = usePostContext();
    const { myFreinds, Notify, activeProfile } = useUserContext();

    const actorName  = activeProfile ? activeProfile.name : `${auth?.fname || ''} ${auth?.lname || ''}`.trim();
    const actorAvatar = activeProfile
        ? (activeProfile.photo || null)
        : (auth?.photo ? (auth.photo.startsWith('http') ? auth.photo : `https://buzzinguniverse.com/backend/${auth.photo}`) : '/images/default.png');
    const actorLink  = activeProfile
        ? `/company/${activeProfile.id}/${encodeURIComponent(activeProfile.name)}`
        : `/member/${auth?.id}/profile`;

    const [comment, setComment] = useState('');
    const [tagIds, setTagIds] = useState([]);
    const [filteredFriends, setFilteredFriends] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [gifSearch, setGifSearch] = useState('');
    const [gifs, setGifs] = useState([]);
    const [gifLoading, setGifLoading] = useState(false);
    const [attachment, setAttachment] = useState(null); // { type: 'image'|'gif', file?, previewUrl?, url? }

    const inputRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const gifPickerRef = useRef(null);
    const gifButtonRef = useRef(null);
    const imageInputRef = useRef(null);
    const typingDebounce = useRef(null);

    useEffect(() => {
        const mentionIds = [...(inputRef.current?.querySelectorAll('.mention') || [])].map(
            (el) => parseInt(el.getAttribute('nav_id'))
        );
        setTagIds(mentionIds);
    }, [comment]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(e.target) &&
                !emojiButtonRef.current?.contains(e.target)
            ) setShowEmojiPicker(false);

            if (
                gifPickerRef.current &&
                !gifPickerRef.current.contains(e.target) &&
                !gifButtonRef.current?.contains(e.target)
            ) setShowGifPicker(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showGifPicker && gifs.length === 0) fetchGifs('trending');
    }, [showGifPicker]);

    const fetchGifs = async (query) => {
        setGifLoading(true);
        try {
            const url = query === 'trending'
                ? `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=18&rating=g`
                : `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(query)}&limit=18&rating=g`;
            const res = await fetch(url);
            const data = await res.json();
            setGifs(data.data || []);
        } catch (err) {
            console.error('GIF fetch error', err);
        } finally {
            setGifLoading(false);
        }
    };

    const handleGifSearchChange = (e) => {
        const q = e.target.value;
        setGifSearch(q);
        if (q.trim().length > 1) fetchGifs(q.trim());
        else if (q.trim().length === 0) fetchGifs('trending');
    };

    const selectGif = (gif) => {
        setAttachment({ type: 'gif', url: gif.images.fixed_height.url });
        setShowGifPicker(false);
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAttachment({ type: 'image', file, previewUrl: URL.createObjectURL(file) });
    };

    const removeAttachment = () => {
        if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
        setAttachment(null);
        if (imageInputRef.current) imageInputRef.current.value = '';
    };

    const insertEmoji = (emojiData) => {
        const el = inputRef.current;
        const newHTML = (el.innerHTML || '') + emojiData.emoji;
        el.innerHTML = newHTML;
        setComment(newHTML);
        el.focus();
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
        setShowEmojiPicker(false);
    };

    const broadcastTyping = () => {
        if (!auth?.id || !post?.post_data?.id || typingDebounce.current) return;
        axios.post('https://buzzinguniverse.com/backend/api/comment-typing', {
            post_id: post.post_data.id,
            user_id: auth.id,
            user_name: actorName,
        }).catch(() => {});
        typingDebounce.current = setTimeout(() => { typingDebounce.current = null; }, 2000);
    };

    const handleInputChange = (e) => {
        const newHtml = e.target.innerHTML;
        const newText = e.target.textContent;
        setComment(newHtml);
        broadcastTyping();

        const atIndex = newText.lastIndexOf('@');
        if (atIndex !== -1) {
            const searchQuery = newText.substring(atIndex + 1);
            if (searchQuery.length > 40 || searchQuery.includes('\n')) {
                setShowDropdown(false);
            } else {
                const q = searchQuery.toLowerCase();
                setFilteredFriends(
                    myFreinds.filter((friend) => {
                        const full = `${friend.user_data.first_name} ${friend.user_data.last_name}`.toLowerCase();
                        return full.includes(q) ||
                            friend.user_data.first_name.toLowerCase().includes(q) ||
                            friend.user_data.last_name.toLowerCase().includes(q);
                    })
                );
                setShowDropdown(true);
            }
        } else {
            setShowDropdown(false);
        }
    };

    const handleMentionClick = (friend) => {
        const textBeforeMention = comment.substring(0, comment.lastIndexOf('@'));
        const mentionHTML = `<span class="mention" nav_id="${friend.id}">@${friend.first_name} ${friend.last_name}</span>&nbsp;`;
        const updatedComment = `${textBeforeMention}${mentionHTML}`;
        inputRef.current.innerHTML = updatedComment;
        setComment(updatedComment);
        setShowDropdown(false);
        setTimeout(() => {
            const el = inputRef.current;
            el.focus();
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }, 0);
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        const hasText = comment.trim() && comment.trim() !== '<br>';
        if (!hasText && !attachment) return;
        setLoading(true);
        try {
            let finalMessage = comment;

            if (attachment) {
                if (attachment.type === 'gif') {
                    finalMessage += `<br><img src="${attachment.url}" class="comment-media-img" alt="gif">`;
                } else if (attachment.type === 'image') {
                    const uploadData = new FormData();
                    uploadData.append('file', attachment.file);
                    const uploadRes = await axios.post('https://buzzinguniverse.com/backend/api/upload-temp', uploadData);
                    if (uploadRes.data.code === 200) {
                        finalMessage += `<br><img src="${uploadRes.data.url}" class="comment-media-img" alt="image">`;
                    }
                }
            }

            const commentPayload = {
                post_id: post.post_data.id,
                user_id: auth?.id,
                message: finalMessage,
                comment_id: comment_id || null,
                tag_ids: tagIds.join(','),
            };
            if (activeProfile) commentPayload.group_id = activeProfile.id;
            const response = await axios.post('https://buzzinguniverse.com/backend/api/comment-request', commentPayload);

            if (response.status === 200) {
                setComment('');
                Notify(post.post_data.user_id, `${actorName} commented on your post`, `${actorLink}/post/${post.post_data.id}`);
                inputRef.current.innerHTML = '';
                setTagIds([]);
                removeAttachment();
                addCommentToPost(post.post_data.id, response.data.data);
                GetPoost && GetPoost();
                setShowCommentBox(false);
            }
        } catch (error) {
            console.error('Error posting comment:', error);
        } finally {
            setLoading(false);
        }
    };

    const hasContent = (comment.trim() && comment.trim() !== '<br>') || !!attachment;

    return (
        <div className="cb-wrapper">
            <UserAvatar
                src={actorAvatar}
                alt="img"
                className="cb-avatar"
                isCompany={!!activeProfile}
            />

            <div className="cb-body">
                <form onSubmit={handleCommentSubmit}>
                    <div className="cb-pill">
                        {/* Text input */}
                        <div
                            ref={inputRef}
                            className="cb-input"
                            contentEditable
                            onInput={handleInputChange}
                            data-placeholder="Add a comment..."
                            style={{ fontFamily: '"Noto Color Emoji", -apple-system, BlinkMacSystemFont, "Segoe UI", "Segoe UI Emoji", "Segoe UI Symbol", "Apple Color Emoji", Roboto, Helvetica, Arial, sans-serif' }}
                        />

                        {/* Action icons */}
                        <div className="cb-actions">
                            {/* Emoji */}
                            <button
                                type="button"
                                ref={emojiButtonRef}
                                className="cb-icon-btn"
                                title="Emoji"
                                onClick={() => { setShowEmojiPicker(p => !p); setShowGifPicker(false); }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/>
                                    <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                    <circle cx="9" cy="10" r="1" fill="currentColor"/>
                                    <circle cx="15" cy="10" r="1" fill="currentColor"/>
                                </svg>
                            </button>

                            {/* GIF */}
                            <button
                                type="button"
                                ref={gifButtonRef}
                                className="cb-icon-btn cb-gif-label"
                                title="GIF"
                                onClick={() => { setShowGifPicker(p => !p); setShowEmojiPicker(false); }}
                            >
                                GIF
                            </button>

                            {/* Image */}
                            <button
                                type="button"
                                className="cb-icon-btn"
                                title="Photo"
                                onClick={() => imageInputRef.current?.click()}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
                                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                                    <path d="M3 16l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <input
                                type="file"
                                ref={imageInputRef}
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageSelect}
                            />

                            {/* Send */}
                            {hasContent && (
                                <button type="submit" className="cb-send-btn" disabled={loading} title="Post">
                                    {loading ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.5 }}>
                                            <circle cx="12" cy="12" r="10"/>
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                        </svg>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Mention dropdown */}
                    {showDropdown && filteredFriends.length > 0 && (
                        <ul className="friends-dropdown">
                            {filteredFriends.map((friend) => {
                                const photo = friend.user_data.photo
                                    ? (friend.user_data.photo.startsWith('http') ? friend.user_data.photo : `https://buzzinguniverse.com/backend/${friend.user_data.photo}`)
                                    : '/images/default.png';
                                return (
                                    <li key={friend.user_data.id} onClick={() => handleMentionClick(friend.user_data)}>
                                        <img src={photo} alt="" className="mention-avatar" onError={e => { e.target.src = '/images/default.png'; }} />
                                        <span>{friend.user_data.first_name} {friend.user_data.last_name}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/* Attachment preview */}
                    {attachment && (
                        <div className="cb-attachment">
                            <img
                                src={attachment.type === 'gif' ? attachment.url : attachment.previewUrl}
                                alt="attachment"
                            />
                            <button type="button" className="cb-attachment-remove" onClick={removeAttachment}>×</button>
                        </div>
                    )}
                </form>

                {/* Emoji picker */}
                {showEmojiPicker && (
                    <div className="cb-emoji-popup" ref={emojiPickerRef}>
                        <EmojiPicker
                            onEmojiClick={insertEmoji}
                            width={300}
                            height={370}
                            theme="light"
                            searchDisabled={false}
                            skinTonesDisabled={false}
                            previewConfig={{ showPreview: false }}
                        />
                    </div>
                )}

                {/* GIF picker */}
                {showGifPicker && (
                    <div className="cb-gif-popup" ref={gifPickerRef}>
                        <input
                            className="cb-gif-search"
                            type="text"
                            placeholder="Search GIFs..."
                            value={gifSearch}
                            onChange={handleGifSearchChange}
                            autoFocus
                        />
                        <div className="cb-gif-grid">
                            {gifLoading && <div className="cb-gif-loading">Loading...</div>}
                            {!gifLoading && gifs.length === 0 && (
                                <div className="cb-gif-loading">No GIFs found</div>
                            )}
                            {!gifLoading && gifs.map((gif) => (
                                <img
                                    key={gif.id}
                                    src={gif.images.fixed_height_small.url}
                                    alt={gif.title}
                                    className="cb-gif-item"
                                    onClick={() => selectGif(gif)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommentBox;
